import { useI18n } from '@fox/extensions/src/use-i18n';
import { useEffect, useState } from 'react';
import { useDebounce } from '../../hooks';
import { strIsNullOrEmpty } from '../../utils';

export async function getGoogleAddressSuggestionsAsync<T>(
	address: string,
	region: string,
	convertSuggestion: (r: google.maps.places.AutocompletePrediction) => T,
	types?: string[]
): Promise<T[]> {
	const geocoder = new google.maps.places.AutocompleteService();
	return new Promise<T[]>((resolve, reject) => {
		try {
			geocoder.getPlacePredictions(
				{
					componentRestrictions: {
						country: region
					},
					input: address,
					...(types && types.length > 0 ? { types } : {})
				},
				(res, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK) {
						resolve(res?.map(r => convertSuggestion(r)) ?? []);
					} else {
						resolve([]);
					}
				}
			);
		} catch (e) {
			reject(e);
		}
	});
}

export async function getGooglePlaceSuggestionsAsync<T>(
	address: string,
	region: string,
	convertSuggestion: (r: google.maps.places.Place) => T | undefined
): Promise<T[]> {
	// TOTAL BS: eslint is smoking crack here, put your mouse over any of the types you see here and it will
	// show you the correct types instead of any or unknown!!! I don't know what you are complaining about!

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
	const { places } = await Place.searchByText({
		fields: ['displayName', 'formattedAddress', 'location', 'viewport', 'addressComponents', 'types'],
		textQuery: address,
		region: region,
		maxResultCount: 10
	});
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
	return places.map(r => convertSuggestion(r)).filter(r => r !== undefined);
}

/**
 * Custom hook that performs a debounced search for Google address suggestions.
 *
 * @template T - The type to which each suggestion will be converted.
 * @param valueToSearch - The input string to search for address suggestions.
 * @param delay - The debounce delay in milliseconds before triggering the search.
 * @param convertSuggestion - A function that converts a Google AutocompletePrediction to the desired type T.
 * @returns The result of the debounced address search, typed as T.
 */
export function useDebouncedGoogleAddressSearch<T>(
	valueToSearch: string,
	delay: number,
	convertSuggestion: (r: google.maps.places.AutocompletePrediction) => T
) {
	return useDebouncedAddressSearch(valueToSearch, delay, (addr, region) =>
		getGoogleAddressSuggestionsAsync(addr, region, convertSuggestion, undefined)
	);
}

/**
 * Custom React hook that performs a debounced address search using a provided fetcher function.
 *
 * @template T The type of the address search result items.
 * @param valueToSearch - The input string to search for addresses.
 * @param delay - The debounce delay in milliseconds before triggering the search.
 * @param fetcher - An asynchronous function that fetches address suggestions based on the input string and region code.
 * @returns A tuple containing:
 *   - The current list of address suggestions (`T[]`),
 *   - A setter function to update the suggestions,
 *   - A boolean indicating whether the fetch operation is in progress.
 *
 * @remarks
 * The hook debounces the search input to avoid excessive API calls and automatically manages the loading state.
 * It also uses the current region code from the i18n context when fetching suggestions.
 */
export function useDebouncedAddressSearch<T>(valueToSearch: string, delay: number, fetcher: (addr: string, region: string) => Promise<T[]>) {
	const i18n = useI18n();
	const [localResults, setLocalResults] = useState<T[]>([]);
	const debouncedAddress = useDebounce(valueToSearch, delay);
	const [isFetching, setIsFetching] = useState<boolean>(false);
	useEffect(() => {
		function querySuggestions() {
			if (!strIsNullOrEmpty(debouncedAddress)) {
				setIsFetching(true);
				fetcher(debouncedAddress, i18n.countryCode)
					.then(list => {
						setLocalResults(list);
						setIsFetching(false);
					})
					.catch(() => setIsFetching(false));
			}
		}
		querySuggestions();
	}, [debouncedAddress]);
	return [localResults, setLocalResults, isFetching] as const;
}
